/**
 * Onboarding Store
 *
 * Zustand store for managing onboarding questionnaire state
 * Tracks completion status and synchronizes across the app
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { OnboardingStatus } from "../types/onboarding";
import { onboardingApi } from "../api/onboarding";

interface OnboardingStore {
  // State
  status: OnboardingStatus | null;
  loading: boolean;
  error: string | null;

  // Actions
  loadStatus: (tenantId: number) => Promise<void>;
  markComplete: (tenantId: number) => Promise<void>;
  reset: (tenantId: number) => Promise<void>;
  setStatus: (status: OnboardingStatus) => void;
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set) => ({
      // Initial state
      status: null,
      loading: false,
      error: null,

      // Load onboarding status from API
      loadStatus: async (tenantId: number) => {
        set({ loading: true, error: null });
        try {
          const status = await onboardingApi.getOnboardingStatus(tenantId);
          set({ status, loading: false });
        } catch (error) {
          console.error("Failed to load onboarding status:", error);
          // On error, set a default completed status to not block users
          const defaultStatus: OnboardingStatus = {
            isComplete: true,
            completedAt: new Date().toISOString(),
            answersCount: 0,
            tenantId,
          };
          set({
            status: defaultStatus,
            error:
              error instanceof Error
                ? error.message
                : "Failed to load onboarding status",
            loading: false,
          });
        }
      },

      // Mark onboarding as complete
      markComplete: async (tenantId: number) => {
        try {
          await onboardingApi.completeOnboarding(tenantId);
          // Update local state
          const newStatus: OnboardingStatus = {
            isComplete: true,
            completedAt: new Date().toISOString(),
            answersCount: 5,
            tenantId,
          };
          set({ status: newStatus });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to complete onboarding",
          });
        }
      },

      // Reset onboarding (for testing)
      reset: async (tenantId: number) => {
        try {
          await onboardingApi.resetOnboarding(tenantId);
          const newStatus: OnboardingStatus = {
            isComplete: false,
            answersCount: 0,
            tenantId,
          };
          set({ status: newStatus });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to reset onboarding",
          });
        }
      },

      // Manually set status
      setStatus: (status: OnboardingStatus) => {
        set({ status });
      },
    }),
    {
      name: "onboarding-storage",
      partialize: (state) => ({ status: state.status }),
    },
  ),
);
